import { forwardRef, Fragment, memo, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { DisclosureNext, DisclosurePrevious } from '@sas/react-svg';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../hooks/useKeyboardTooltip';
import { useMounted } from '../../hooks/useMounted';
import { useRefArray } from '../../hooks/useRefArray';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { isArSaLocale, isThaiLocale } from '../../locale/localeUtils';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { markEvent } from '../../util/events';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { useDefaultDateTimeFormat } from '../DatePicker';
import { convertGregorianYearToThaiYear, convertThaiYearToGregorianYear, getDaysOfWeek, getMonthAndYearByDate, getMonthsForLocale, getYearOrder, isSameDay } from '../DatePicker/DateUtil';
import { Input, useNumericValidationAdapter, validateNumericInput } from '../Input';
import { InvisibleText } from '../InvisibleText';
import { Select } from '../Select';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
// avoid cycle dependencies
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { CalendarLegend } from './__internal__/CalendarLegend';
import useSelectedAriaDescribedby from './__internal__/useSelectedAriaDescribedby';
import { createDisabledDayFilter, isAfterDate, isPastDate } from './DisabledDayFilters';
import { getCalendarRows, getCalendarWeekNumber } from './GregorianCalendarFacts';
import bundle from './NovaComponents-Calendar-gui-icu.properties';
import cn from './Calendar.css.json';
import './Calendar.css';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useCalendarId = createUseHtmlId('calendar');
const useCalendarSelectionId = createUseHtmlId('Calendar-selection');
const useCalendarSelectionStateId = createUseHtmlId('Calendar-selection-state');
const useCalendarIntervalStartId = createUseHtmlId('Calendar-interval-start');
const useCalendarIntervalEndId = createUseHtmlId('Calendar-interval-end');
const useCalendarSelectId = createUseHtmlId('Calendar-select');
const useCalendarTodayId = createUseHtmlId('Calendar-today');
const useCalendarSpecialDayId = createUseHtmlId('Calendar-special-day');
const useCalendarNonWorkDayId = createUseHtmlId('Calendar-non-workday');
const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('Calendar'); // eslint-disable-line @sas/i18n/jsx-to-localize

const defaultMinDate = new Date(1582, 0, 1);
const defaultMaxDate = new Date(9999, 11, 31);
let minDate;
let maxDate;
export const Calendar = /*#__PURE__*/forwardRef(function Calendar({
  onChange,
  intervalSelection,
  ...props
}, ref) {
  const dayFormat = useDefaultDateTimeFormat({
    type: 'dayOnly'
  });
  const monthFormat = useDefaultDateTimeFormat({
    type: 'monthOnly'
  });
  const isCurrentMonth = useCallback((day, rowIndex) => {
    if (parseInt(dayFormat(day)) > 7 && rowIndex === 0) return false;
    if (parseInt(dayFormat(day)) <= 7 && rowIndex >= 4) return false;
    return true;
  }, [dayFormat]);
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const today = useMemo(() => {
    return new Date();
  }, []);
  const dayOfWeekIds = useCalendarId(7);
  const isRTL = useIsRTL();
  const mountedRef = useMounted();
  const singleSelection = props.singleSelection ?? true;
  const [prevDateRangeDate, setPrevDateRangeDate] = useState(props.dateRange);
  const [dateRangeDates, setDateRangeDates] = useState(props.dateRange ? props.dateRange : []);
  const [selectedDates, setSelectedDates] = useState([]);
  const nonWorkDays = props.nonWorkDays ?? [0, 6];
  minDate = props.minDate && isAfterDate(props.minDate, {
    today: defaultMinDate
  }) && (isPastDate(props.minDate, {
    today: defaultMaxDate
  }) || isSameDay(props.minDate, defaultMaxDate)) ? new Date(props.minDate) : new Date(defaultMinDate);
  minDate.setHours(0, 0, 0, 0);
  maxDate = props.maxDate && (isAfterDate(props.maxDate, {
    today: defaultMinDate
  }) || isSameDay(props.maxDate, defaultMinDate)) && isPastDate(props.maxDate, {
    today: defaultMaxDate
  }) ? new Date(props.maxDate) : new Date(defaultMaxDate);
  maxDate.setHours(23, 59, 59);
  if (props.minDate && props.maxDate && props.minDate.getTime() > props.maxDate.getTime()) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn('minDate > MaxDate -> dates switched');
    const tempMaxDate = new Date(props.minDate.getTime());
    const tempMinDate = new Date(props.maxDate.getTime());
    minDate = new Date(tempMinDate.getTime());
    maxDate = new Date(tempMaxDate.getTime());
  }
  const firstValidDate = useMemo(() => {
    const dateVal = props.value ?? new Date();
    if (props.minDate && dateVal.getTime() < props.minDate.getTime()) {
      return new Date(props.minDate);
    } else if (props.maxDate && dateVal.getTime() > props.maxDate.getTime()) {
      return new Date(props.openOnMinDate && props.minDate ? props.minDate : props.maxDate);
    }
    return dateVal;
  }, [props.minDate, props.maxDate, props.openOnMinDate, props.value]);

  // useEffect will select first valid date and updates datePicker's input field.
  useEffect(() => {
    if (props.value && (props.minDate || props.maxDate) && !isSameDay(props.value, firstValidDate)) {
      onChange?.({
        value: firstValidDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isBeforeMinDate = day => isPastDate(day, {
    today: minDate
  });
  const isAfterMaxDate = day => isAfterDate(day, {
    today: maxDate
  });
  const isNonWorkDay = day => {
    if (!props.showNonWorkDays) {
      return false;
    }
    return nonWorkDays.some(nonWorkDay => day.getDay() === nonWorkDay);
  };
  const isSpecialDay = day => {
    if (!props.specialDates) {
      return false;
    }
    const isSpecialDate = specialDate => {
      if (specialDate.startDate && specialDate.endDate) {
        const startDate = specialDate.startDate;
        const endDate = specialDate.endDate;
        if (startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
          return true;
        }
      } else {
        const startDate = specialDate.startDate;
        return startDate && isSameDay(startDate, day);
      }
      return false;
    };
    return props.specialDates.dateRange.some(specialDate => isSpecialDate(specialDate));
  };
  const disabledDayFilter = useMemo(() => {
    if (!props.disabledDays) {
      return undefined;
    } else if (typeof props.disabledDays === 'function') {
      return props.disabledDays;
    } else {
      return createDisabledDayFilter(props.disabledDays);
    }
  }, [props.disabledDays]);
  const isDisabledDay = useCallback(day => {
    let returnVal = false;
    if (isBeforeMinDate(day) || isAfterMaxDate(day)) returnVal = true;
    if (!returnVal && props.disabledDates) {
      returnVal = props.disabledDates.some(disabledDate => isSameDay(disabledDate, day));
    }
    if (!returnVal && props.enabledDates) {
      returnVal = props.enabledDates.every(enabledDate => !isSameDay(enabledDate, day));
    }
    if (!returnVal && disabledDayFilter) {
      returnVal = disabledDayFilter(day, {
        today
      });
    }
    return returnVal;
  }, [disabledDayFilter, props.disabledDates, props.enabledDates, today]);
  const isValidSingleSelection = date => {
    if (!isBeforeMinDate(date) && !isAfterMaxDate(date)) {
      if (mountedRef.current && singleSelection && !intervalSelection && selectedDates.length > 0) return true;
    }
    return false;
  };
  const [state, setState] = useState(props.value ?? new Date());
  const [forceDayCellFocus, setForceDayCellFocus] = useState(false);
  const [focusState, dispatchFocusAction] = useReducer((prevState, action) => {
    switch (action.type) {
      case 'step':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0);
          let delta = 0;
          if (action.direction === 'up') {
            delta = -7;
          } else if (action.direction === 'down') {
            delta = 7;
          } else if (action.direction === 'left') {
            delta = isRTL ? 1 : -1;
          } else if (action.direction === 'right') {
            delta = isRTL ? -1 : 1;
          } else if (action.direction === 'startOfWeek') {
            delta = nextDate.getDay() * -1;
          } else if (action.direction === 'endOfWeek') {
            delta = 6 - nextDate.getDay();
          }
          nextDate.setDate(nextDate.getDate() + delta);
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          if (nextDate.getMonth() !== prevState.getMonth()) {
            const dte = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
            if (dte.getMonth() !== nextDate.getMonth()) {
              while (dte.getMonth() !== nextDate.getMonth()) dte.setDate(dte.getDate() - 1);
            }
            if (isValidSingleSelection(dte)) {
              onChange?.({
                value: dte
              });
            }
          }
          return nextDate;
        }
      case 'monthStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0);
          if (action.direction === 'next') {
            // See if current date is greater than number of days in new month, if so, set date to last date of new month
            const testDate = new Date(nextDate.getFullYear(), nextDate.getMonth() + 2, 0).getDate();
            if (nextDate.getDate() > testDate) nextDate.setDate(testDate);
            nextDate.setMonth(nextDate.getMonth() + 1);
          } else if (action.direction === 'previous') {
            // See if current date is greater than number of days in new month, if so, set date to last date of new month
            const testDate = new Date(nextDate.getFullYear(), nextDate.getMonth(), 0).getDate();
            if (nextDate.getDate() > testDate) nextDate.setDate(testDate);
            nextDate.setMonth(nextDate.getMonth() - 1);
          }
          const lastDate = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
          const newDate = new Date(nextDate);
          if (isDisabledDay(newDate)) {
            // First go backwards to find an enabled date
            while (isDisabledDay(newDate) && newDate.getDate() !== 1) {
              newDate.setDate(newDate.getDate() - 1);
              if (!isDisabledDay(newDate)) nextDate.setDate(newDate.getDate());
            }
            if (isDisabledDay(newDate)) {
              // If no enabled date going backwards, now go forward to find an enabled date
              while (isDisabledDay(newDate) && newDate.getDate() < lastDate) {
                newDate.setDate(newDate.getDate() + 1);
                if (!isDisabledDay(newDate)) nextDate.setDate(newDate.getDate());
              }
            }
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte = new Date(nextDate.getFullYear(), nextDate.getMonth(), newDate.getDate());
          if (dte.getMonth() !== nextDate.getMonth()) {
            while (dte.getMonth() !== nextDate.getMonth()) dte.setDate(dte.getDate() - 1);
          }
          if (isValidSingleSelection(dte)) {
            onChange?.({
              value: dte
            });
          }
          return nextDate;
        }
      case 'yearStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0);
          if (action.direction === 'next') {
            nextDate.setFullYear(nextDate.getFullYear() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setFullYear(nextDate.getFullYear() - 1);
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
          if (dte.getMonth() !== nextDate.getMonth()) {
            while (dte.getMonth() !== nextDate.getMonth()) dte.setDate(dte.getDate() - 1);
          }
          if (isValidSingleSelection(dte)) {
            onChange?.({
              value: dte
            });
          }
          return nextDate;
        }
      case 'decadeStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0);
          if (action.direction === 'next') {
            nextDate.setFullYear(nextDate.getFullYear() + 10);
          } else if (action.direction === 'previous') {
            nextDate.setFullYear(nextDate.getFullYear() - 10);
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
          if (dte.getMonth() !== nextDate.getMonth()) {
            while (dte.getMonth() !== nextDate.getMonth()) dte.setDate(dte.getDate() - 1);
          }
          if (isValidSingleSelection(dte)) {
            onChange?.({
              value: dte
            });
          }
          return nextDate;
        }
      case 'monthSelect':
        // See if prevState date greater than number of days in new month, if so, set date to last date of new month
        let year = action.value.year;
        if (isNaN(year)) year = prevState.getFullYear();
        if (isThaiLocale(locale)) year = convertThaiYearToGregorianYear(year);
        const lastDate = new Date(year, action.value.month + 1, 0).getDate();
        let newDate;
        if (prevState.getDate() > lastDate) {
          newDate = new Date(year, action.value.month, lastDate);
        } else newDate = new Date(year, action.value.month, prevState.getDate());
        const tmpDate = new Date(newDate);
        if (isDisabledDay(tmpDate)) {
          // First go backwards to find an enabled date
          while (isDisabledDay(tmpDate) && tmpDate.getDate() !== 1) {
            tmpDate.setDate(tmpDate.getDate() - 1);
            if (!isDisabledDay(tmpDate)) newDate.setDate(tmpDate.getDate());
          }
          if (isDisabledDay(tmpDate)) {
            // If no enabled date going backwards, now go forward to find an enabled date
            while (isDisabledDay(tmpDate) && tmpDate.getDate() < lastDate) {
              tmpDate.setDate(tmpDate.getDate() + 1);
              if (!isDisabledDay(tmpDate)) newDate.setDate(tmpDate.getDate());
            }
          }
        }
        const dte = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
        if (dte.getMonth() !== newDate.getMonth()) {
          while (dte.getMonth() !== newDate.getMonth()) dte.setDate(dte.getDate() - 1);
        }
        if (isValidSingleSelection(dte)) {
          if (isArSaLocale(locale) && typeof action.value.day === 'number') dte.setDate(action.value.day);
          // SetTimeout was added to avoid a bad state on first render, when the user selects a different month --> see NOVACOMMONS-13238
          setTimeout(() => {
            onChange?.({
              value: dte
            });
          }, 0);
        }
        if (isArSaLocale(locale) && typeof action.value.day === 'number') newDate.setDate(action.value.day);
        return newDate;
      case 'selection':
        return action.value;
      default:
        return prevState;
    }
  }, firstValidDate);
  const calendar = useMemo(() => getCalendarRows(focusState.getMonth(), focusState.getFullYear(), locale), [focusState, locale]);
  const formatedCalendar = useMemo(() => {
    if (!isArSaLocale(locale)) return calendar;
    const dayOneIndex = calendar.findIndex(row => row.find(day => {
      const isItem = day.getDate() === focusState.getDate() && day.getMonth() === focusState.getMonth();
      return isItem;
    }));
    let startIndex = 0;
    const formatedDate = parseInt(dayFormat(focusState));
    if (formatedDate >= 1 && formatedDate <= 7) {
      const temp = calendar[dayOneIndex].find(day => parseInt(dayFormat(day)) === 1);
      if (temp) {
        startIndex = dayOneIndex;
      } else {
        startIndex = dayOneIndex - 1;
      }
    } else {
      for (let i = dayOneIndex - 1; i >= dayOneIndex - 5; i--) {
        const temp = calendar[i].find(day => parseInt(dayFormat(day)) === 1);
        if (temp) {
          startIndex = i;
          break;
        }
      }
    }
    let endIndex = startIndex + 5;
    for (let i = endIndex; i >= endIndex - 1; i--) {
      const temp = calendar[i]?.findIndex(day => parseInt(dayFormat(day)) === 1);
      if (temp >= 0) {
        endIndex = temp === 0 ? i - 1 : i;
        break;
      }
    }
    const formatedCal = calendar.filter((_row, index) => index >= startIndex && index <= endIndex);
    return formatedCal;
  }, [focusState, dayFormat, calendar, locale]);
  const weekNumberIds = useCalendarId(calendar.length);
  const {
    refs: dayCells,
    createSetRef: createSetDayCell
  } = useRefArray();
  const refDayCellContainer = useRef(null);
  const headerRef = useRef(null);
  const [hasDayCellFocus, setHasDayCellFocus] = useState(false);
  // focus management for the day cell grid
  {
    const refPrevFocusState = useRef(focusState);
    useEffect(() => {
      if (!forceDayCellFocus && (refPrevFocusState.current === focusState || !hasDayCellFocus)) {
        return;
      }
      refPrevFocusState.current = focusState;
      function getDayCell(day) {
        for (let index = 0; index < formatedCalendar.length * 7; ++index) {
          const currentDay = formatedCalendar[Math.floor(index / 7)][index % 7];
          if (isSameDay(currentDay, day)) {
            return dayCells[index];
          }
        }
      }
      getDayCell(focusState)?.focus();
      setForceDayCellFocus(false);
    }, [focusState, dayCells, formatedCalendar, hasDayCellFocus, ref, forceDayCellFocus]);
  }
  const weekNumber = useMemo(() => props.showWeekNumbers ? getCalendarWeekNumber(focusState.getFullYear(), focusState.getMonth()) : NaN, [focusState, props.showWeekNumbers]);
  const getWeekNum = (week, monthIndex, year) => {
    const d = 1 + (week - 1) * 7; // 1st of January + 7 days for each week
    const date = new Date(year, 0, d);
    const first = date.getDate() - date.getDay(); // First day of the week
    const last = first + 6; // last day is the first day + 6
    const lastDate = new Date(date.setDate(last));
    if (monthIndex === 11 && lastDate.getMonth() === 0) return 1;
    return week;
  };
  const [focusVisible, setFocusVisible] = useState(undefined);
  const isSelectedDate = useCallback(day => {
    if (selectedDates && selectedDates.length > 0) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (singleSelection && intervalSelection && startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
        return true;
      }
      if (!singleSelection && !intervalSelection) {
        return selectedDates.some(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, day));
      }
    }
    if (dateRangeDates) {
      const startDate = dateRangeDates[0]?.startDate;
      const endDate = dateRangeDates[0]?.endDate;
      if (singleSelection && intervalSelection && startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
        return true;
      }
      if (!singleSelection && !intervalSelection) {
        return dateRangeDates.some(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, day));
      }
    }
    return false;
  }, [dateRangeDates, intervalSelection, selectedDates, singleSelection]);
  const hasIntervalDates = () => {
    if (selectedDates && selectedDates[0]?.startDate && selectedDates[0]?.endDate) return true;
    if (dateRangeDates && dateRangeDates[0]?.startDate && dateRangeDates[0]?.endDate) return true;
    return false;
  };
  const isDateWithinInterval = day => {
    if (selectedDates && selectedDates[0]?.startDate && selectedDates[0]?.endDate) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (day.getTime() > startDate.getTime() && endDate && day.getTime() < endDate.getTime()) {
        return true;
      }
    }
    if (dateRangeDates) {
      const startDate = dateRangeDates[0]?.startDate;
      const endDate = dateRangeDates[0]?.endDate;
      if (startDate && endDate && day.getTime() > startDate.getTime() && endDate && day.getTime() < endDate.getTime()) {
        return true;
      }
    }
    return false;
  };
  const {
    ariaSelectedText,
    ariaSelectedDate,
    handleAriaDateSelection,
    resetAllAriaSelected
  } = useSelectedAriaDescribedby();
  const handleSelectionType = useCallback(value => {
    if (isDisabledDay(value)) return;
    const newDate = new Date(value);
    newDate.setHours(0, 0, 0, 0);
    const isSelected = isSelectedDate(newDate);
    const dateRange = [];
    setDateRangeDates([]);
    if (!intervalSelection) {
      handleAriaDateSelection(value, isSelected);
    }
    if (singleSelection) {
      if (intervalSelection) {
        if (selectedDates[0]?.startDate && !selectedDates[0]?.endDate) {
          if (newDate.getTime() < selectedDates[0].startDate.getTime()) {
            dateRange.push({
              startDate: newDate,
              endDate: selectedDates[0].startDate
            });
            setSelectedDates(dateRange);
          } else if (newDate.getTime() >= selectedDates[0].startDate.getTime()) {
            dateRange.push({
              startDate: selectedDates[0].startDate,
              endDate: newDate
            });
            setSelectedDates(dateRange);
          }
        } else {
          dateRange.push({
            startDate: newDate,
            endDate: undefined
          });
          setSelectedDates(dateRange);
        }
        onChange?.({
          value: new Date(value),
          dateRange
        });
      } else {
        selectedDates.length = 0;
        selectedDates.push({
          startDate: newDate,
          endDate: undefined
        });
        onChange?.({
          value: new Date(value)
        });
      }
    } else {
      // Check if date is currently selected and remove if so
      if (isSelected) {
        const index = selectedDates?.findIndex(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, newDate));
        if (index >= 0) {
          selectedDates?.splice(index, 1);
          onChange?.({
            value: null,
            dateRange: selectedDates
          });
        }
      } else {
        selectedDates.push({
          startDate: newDate,
          endDate: undefined
        });
        onChange?.({
          value: new Date(newDate),
          dateRange: selectedDates
        });
      }
    }
  }, [intervalSelection, isDisabledDay, isSelectedDate, onChange, selectedDates, singleSelection, handleAriaDateSelection]);
  const onDayCellPress = useCallback(value => {
    if (isDisabledDay(value)) return;
    dispatchFocusAction({
      type: 'selection',
      value
    });
    setState(new Date(value));
    handleSelectionType(value);
  }, [handleSelectionType, isDisabledDay]);
  const onTodayButtonPress = value => {
    setForceDayCellFocus(true);
    setHasDayCellFocus(true);
    dispatchFocusAction({
      type: 'selection',
      value
    });
    if (!isDisabledDay(value)) {
      setState(new Date(value));
      handleSelectionType(value);
    }
    props.onTodayButtonPress?.(value);
    // using setTimeout because reset depends on focus state which is updated asynchronously
    setTimeout(() => headerRef.current?.reset(), 0);
  };
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const tokens = getCalendarDesignTokens(dt, {
    showTodayButton: props.showTodayButton,
    showLegend: props.showLegend,
    formatedCalendar,
    isNewTokenValueFlagEnabled
  });
  const divRef = useRef(null);
  const selectionTextId = useCalendarSelectionId();
  const selectionStateTextId = useCalendarSelectionStateId();
  const invisibleTextStartId = useCalendarIntervalStartId();
  const invisibleTextEndId = useCalendarIntervalEndId();
  const invisibleSelectId = useCalendarSelectId();
  const getAriaDescribedBy = day => {
    const ariaDescribedBy = [];
    if (intervalSelection) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (startDate && endDate && (isSameDay(day, startDate) || isSameDay(day, endDate))) {
        ariaDescribedBy.push(invisibleTextEndId);
      }
      if (startDate && !endDate && isSameDay(day, startDate)) {
        ariaDescribedBy.push(invisibleTextStartId);
      }
    }
    if (!intervalSelection && ariaSelectedDate && isSameDay(day, ariaSelectedDate)) {
      ariaDescribedBy.push(invisibleSelectId);
    }
    return ariaDescribedBy.join(' ');
  };
  const getSelectedAriaDescribedBy = () => {
    const ariaSelectedDescribedBy = [];
    if (selectedDates && selectedDates.length > 0) {
      ariaSelectedDescribedBy.push(selectionStateTextId);
    }
    ariaSelectedDescribedBy.push(selectionTextId);
    return ariaSelectedDescribedBy.join(' ');
  };
  const getSelectStateText = useCallback(() => {
    if (intervalSelection) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (!startDate || !endDate) return '';
      return localize('interval.endDate.aria', {
        startDate: startDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) ?? '',
        endDate: endDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) ?? ''
      });
    } else if (!singleSelection) {
      const selDates = [];
      if (selectedDates && selectedDates.length > 0) {
        // Sort dates from lowest to highest
        const sortedArray = [...selectedDates];
        sortedArray.sort((a, b) => {
          const aTime = a.startDate?.getTime() ?? 0;
          const bTime = b.startDate?.getTime() ?? 0;
          return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
        });
        sortedArray.forEach(element => selDates.push(element.startDate?.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })));
        return localize('multipleSelectState.aria', {
          dates: selDates?.join(', ')
        });
      } else {
        return '';
      }
    } else {
      if (selectedDates && selectedDates.length > 0) {
        return localize('singleSelectState.aria', {
          date: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) ?? ''
        });
      } else {
        return '';
      }
    }
  }, [intervalSelection, localize, selectedDates, singleSelection]);
  useEffect(() => {
    if (singleSelection && intervalSelection && props.dateRange) {
      if (prevDateRangeDate && prevDateRangeDate.length > 0 && props.dateRange[0].startDate && prevDateRangeDate[0].startDate && props.dateRange[0].endDate && prevDateRangeDate[0].endDate && (!isSameDay(props.dateRange[0].startDate, prevDateRangeDate[0].startDate) || !isSameDay(props.dateRange[0].endDate, prevDateRangeDate[0].endDate))) {
        // We have a new props.dateRange setting.  Set the new dateRangeDates
        // and clear out the previous selectedDates.
        setSelectedDates([]);
        setDateRangeDates(props.dateRange);
        setPrevDateRangeDate(props.dateRange);
      } else {
        if (selectedDates.length === 0) {
          selectedDates.push({
            startDate: props.dateRange[0].startDate,
            endDate: props.dateRange[0].endDate
          });
        }
      }
    }
  }, [intervalSelection, prevDateRangeDate, props.dateRange, selectedDates, singleSelection]);
  useEffect(() => {
    if (props.value && !intervalSelection && selectedDates && selectedDates.length === 0) {
      setSelectedDates([{
        startDate: props.value,
        endDate: undefined
      }]);
    }
  }, [intervalSelection, props.value, selectedDates]);

  // Check if a custom focus indicator is set. If so, we'll display the focus indicator
  // regardless of mouse/keyboard interaction
  const customFocusIndicator = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';
  return /*#__PURE__*/_jsxs("div", {
    ref: divRef,
    "data-testid": props.testId,
    className: cn.container,
    style: tokens,
    role: "group",
    "aria-label": props.ariaLabel ?? localize('calendar.aria'),
    "aria-labelledby": props.ariaLabelledBy,
    onKeyDown: e => {
      if (e.key === 'Enter') {
        // Marking this event is needed when DatePicker/Calendar is in a Dialog so that Enter doesn't trigger the Dialog's default button
        markEvent(e);
      }
    },
    onMouseDown: e => {
      if (e.button === 2) {
        // disable contextmenu on rmb
        e.preventDefault();
      }
    },
    children: [intervalSelection && /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(InvisibleText, {
        id: invisibleTextStartId,
        testId: postfixTestId(props.testId, '-interval-start'),
        children: localize('interval.startDate.aria', {
          startDate: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) ?? ''
        })
      }), /*#__PURE__*/_jsx(InvisibleText, {
        id: invisibleTextEndId,
        testId: postfixTestId(props.testId, '-interval-end'),
        children: localize('interval.endDate.aria', {
          startDate: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) ?? '',
          endDate: selectedDates[0]?.endDate?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) ?? ''
        })
      })]
    }), /*#__PURE__*/_jsxs("div", {
      role: "grid",
      ref: refDayCellContainer,
      className: cn.calendar,
      "aria-label": localize('calendar.aria'),
      "aria-describedby": getSelectedAriaDescribedBy(),
      onFocusCapture: () => {
        setFocusVisible(prev => prev === undefined || customFocusIndicator ? true : prev);
        setHasDayCellFocus(true);
      },
      onBlurCapture: event => {
        // only consider this a focus loss if the focus is moving outside of this div
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocusVisible(undefined);
          setHasDayCellFocus(false);
        }
      },
      onMouseDown: () => {
        setFocusVisible(customFocusIndicator ? true : false);
      },
      onKeyDown: () => {
        setFocusVisible(true);
      },
      children: [/*#__PURE__*/_jsx(InvisibleText, {
        id: selectionStateTextId,
        testId: postfixTestId(props.testId, '-selection-state'),
        children: getSelectStateText()
      }), /*#__PURE__*/_jsx(InvisibleText, {
        id: selectionTextId,
        children: localize(intervalSelection ? 'intervalSelect.msg' : !singleSelection ? 'multiSelect.msg' : 'singleSelect.msg')
      }), !intervalSelection && ariaSelectedText ? /*#__PURE__*/_jsx(InvisibleText, {
        testId: postfixTestId(props.testId, '-selected-text-description'),
        id: invisibleSelectId,
        children: ariaSelectedText
      }) : null, /*#__PURE__*/_jsx("div", {
        role: "row",
        className: cx(cn.weekdayHeader, props.showWeekNumbers && cn.withWeekNumbers),
        children: /*#__PURE__*/_jsx(WeekdayHeaderCells, {
          dayOfWeekIds: dayOfWeekIds,
          showWeekNumbers: props.showWeekNumbers
        })
      }), formatedCalendar.map((row, rowIndex) => /*#__PURE__*/_jsxs("div", {
        role: "row",
        className: cx(cn.monthRow, props.showWeekNumbers && cn.withWeekNumbers),
        onKeyDown: event => {
          switch (event.key) {
            case 'ArrowDown':
              dispatchFocusAction({
                type: 'step',
                direction: 'down'
              });
              break;
            case 'ArrowUp':
              dispatchFocusAction({
                type: 'step',
                direction: 'up'
              });
              break;
            case 'ArrowLeft':
              dispatchFocusAction({
                type: 'step',
                direction: 'left'
              });
              break;
            case 'ArrowRight':
              dispatchFocusAction({
                type: 'step',
                direction: 'right'
              });
              break;
            case 'Home':
              dispatchFocusAction({
                type: 'step',
                direction: 'startOfWeek'
              });
              break;
            case 'End':
              dispatchFocusAction({
                type: 'step',
                direction: 'endOfWeek'
              });
              break;
            case 'PageUp':
              if (event.shiftKey && event.ctrlKey) dispatchFocusAction({
                type: 'decadeStep',
                direction: 'previous'
              });else if (event.shiftKey) dispatchFocusAction({
                type: 'yearStep',
                direction: 'previous'
              });else dispatchFocusAction({
                type: 'monthStep',
                direction: 'previous'
              });
              break;
            case 'PageDown':
              if (event.shiftKey && event.ctrlKey) dispatchFocusAction({
                type: 'decadeStep',
                direction: 'next'
              });else if (event.shiftKey) dispatchFocusAction({
                type: 'yearStep',
                direction: 'next'
              });else dispatchFocusAction({
                type: 'monthStep',
                direction: 'next'
              });
              break;
            case 'Enter': /* falls-through */
            case ' ':
              const newDate = new Date(focusState);
              if (isBeforeMinDate(newDate) || isAfterMaxDate(newDate)) return;
              setState(newDate);
              handleSelectionType(newDate);
              break;
            default:
          }
        },
        children: [props.showWeekNumbers && /*#__PURE__*/_jsx("div", {
          id: `${weekNumberIds[rowIndex]}`,
          role: "rowheader",
          "aria-label": localize('week.aria', {
            weekNumber: String(getWeekNum(weekNumber + rowIndex, focusState.getMonth(), focusState.getFullYear()))
          }),
          className: cn.weekNumberCell,
          children: getWeekNum(weekNumber + rowIndex, focusState.getMonth(), focusState.getFullYear())
        }), row.map((day, colIndex) => /*#__PURE__*/_jsx(DayCell, {
          testId: `calendarDay(${day.getMonth()}/${day.getDate()}/${day.getFullYear()})`,
          ref: e => {
            setRef(createSetDayCell(rowIndex * 7 + colIndex), e);
          },
          day: day,
          today: isCurrentMonth(day, rowIndex) && isSameDay(day, today),
          selected: singleSelection && intervalSelection && hasIntervalDates() || !singleSelection && selectedDates.length > 0 ? isSelectedDate(day) : props.value ? singleSelection && (isSameDay(day, props.value) || isSameDay(day, firstValidDate)) : false,
          inInterval: singleSelection && intervalSelection && hasIntervalDates() ? isDateWithinInterval(day) : false,
          focused: isSameDay(day, focusState)
          // NOTE: DayCell is memoed, so it's best to add the isSameDay check
          ,
          focusVisible: isSameDay(day, focusState) && !!focusVisible,
          disabled: isDisabledDay(day),
          inCurrentMonth: isArSaLocale(locale) ? isCurrentMonth(day, rowIndex) : day.getMonth() === focusState.getMonth()
          // FIXME: use formatter
          ,
          ariaLabel: day.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          ariaDescribedBy: getAriaDescribedBy(day),
          onPress: onDayCellPress,
          onBlur: () => {
            // clear out text to prevent being read twice
            resetAllAriaSelected();
          },
          nonWorkDay: isNonWorkDay(day),
          specialDay: isSpecialDay(day),
          nonWorkDayAriaLabel: props.nonWorkDayLegendText,
          specialDayAriaLabel: props.specialDates?.legendText,
          intervalSelection: intervalSelection,
          singleSelection: singleSelection,
          children: isArSaLocale(locale) ? isCurrentMonth(day, rowIndex) ? dayFormat(day) : '' : day.getMonth() === focusState.getMonth() ? day.getDate() : ''
        }, colIndex))]
      }, rowIndex))]
    }), props.showTodayButton ? /*#__PURE__*/_jsx("div", {
      className: cn.todayButtonFooter,
      children: /*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.Small,
        text: localize('today.label'),
        onPress: () => {
          onTodayButtonPress(today);
        },
        ariaLabel: localize('today.label'),
        testId: postfixTestId(props.testId, 'todayButton'),
        className: cn.todayButton
      })
    }) : null, /*#__PURE__*/_jsx(MonthCalendarHeader, {
      ref: element => {
        setRef(ref, element);
        setRef(headerRef, element);
      },
      month: focusState.getMonth(),
      focusDate: focusState.getDate(),
      formatedMonth: parseInt(monthFormat(focusState)) - 1,
      year: isThaiLocale(locale) ? convertGregorianYearToThaiYear(focusState.getFullYear()) : focusState.getFullYear(),
      onChange: value => dispatchFocusAction({
        type: 'monthSelect',
        value
      }),
      onPreviousButtonPress: value => props.onPreviousButtonPress?.(value),
      onNextButtonPress: value => props.onNextButtonPress?.(value),
      onMonthSelect: value => props.onMonthSelect?.(value),
      onYearChange: value => props.onYearChange?.(value),
      selectClassName: props.selectClassName
    }), props.showLegend ? /*#__PURE__*/_jsx(CalendarLegend, {
      specialDayLegendText: props.specialDates?.legendText,
      nonWorkDayLegendText: props.nonWorkDayLegendText,
      legendItems: props.legendItems,
      testId: postfixTestId(props.testId, 'legend')
    }) : null]
  });
});
export const MonthCalendarHeader = /*#__PURE__*/forwardRef(function MonthCalendarHeader(props, ref) {
  const dayFormat = useDefaultDateTimeFormat({
    type: 'dayOnly'
  });
  const monthFormat = useDefaultDateTimeFormat({
    type: 'monthOnly'
  });
  const yearFormat = useDefaultDateTimeFormat({
    type: 'yearOnly'
  });
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const minMonthAllowed = minDate.getMonth();
  const maxMonthAllowed = maxDate.getMonth();
  const minMonthFormated = parseInt(monthFormat(minDate));
  const maxMonthFormated = parseInt(monthFormat(maxDate));
  const formatedMinYear = parseInt(yearFormat(minDate));
  const formatedMaxYear = parseInt(yearFormat(maxDate));
  const minYearAllowed = isArSaLocale(locale) ? formatedMinYear : minDate.getFullYear();
  const maxYearAllowed = isArSaLocale(locale) ? formatedMaxYear : maxDate.getFullYear();
  const yearOrder = useMemo(() => getYearOrder(locale), [locale]);
  const months = useMemo(() => getMonthsForLocale(locale), [locale]);
  const [yearValue, setYearValue] = useState(String(props.year));
  const [yearValue2, setYearValue2] = useState(null);
  const yearValueFormated = useMemo(() => {
    const year = yearValue2 ?? yearFormat(new Date(props.year, props.month, props.focusDate));
    return year;
  }, [props.month, props.focusDate, yearFormat, yearValue2, props.year]);
  const monthValueFormated = parseInt(monthFormat(new Date(props.year, props.month, props.focusDate)));
  const [isValid, setIsValid] = useState(true);
  const [isMonthValid, setIsMonthValid] = useState(true);
  const mountedRef = useMounted();
  const validateYearInput = useMemo(() => (value, appLocale, isRequired, allowIntegersOnly, isLive, minLength) => {
    const result = validateNumericInput(value, appLocale, isRequired, allowIntegersOnly, isLive, isArSaLocale(locale) ? 3 : minLength);
    const maxLength = isThaiLocale(locale) ? 5 : 4;
    const minValidationYear = isThaiLocale(locale) ? convertGregorianYearToThaiYear(minYearAllowed).toString() : isArSaLocale(locale) ? formatedMinYear : minYearAllowed.toString();
    const maxValidationYear = isThaiLocale(locale) ? convertGregorianYearToThaiYear(maxYearAllowed).toString() : isArSaLocale(locale) ? formatedMaxYear : maxYearAllowed.toString();
    if (result.isError) {
      result.message = localize('minMaxYear.error.msg', {
        min: minValidationYear.toString(),
        max: maxValidationYear.toString()
      });
      if (mountedRef.current) setIsValid(false);
    } else {
      let yearValue = parseInt(value);
      if (isThaiLocale(locale)) yearValue = convertThaiYearToGregorianYear(yearValue);
      if (value.includes(' ') || !isLive && (yearValue < minYearAllowed || yearValue > maxYearAllowed) || isLive && (yearValue.toString().length === maxLength || value.length === maxLength) && (yearValue < minYearAllowed || yearValue > maxYearAllowed)) {
        result.isError = true;
        result.message = localize('minMaxYear.error.msg', {
          min: minValidationYear.toString(),
          max: maxValidationYear.toString()
        });
        if (mountedRef.current) setIsValid(false);
      } else if (mountedRef.current && value.length > 3 || isArSaLocale(locale) && value.length >= 3) {
        setIsValid(true);
      }
    }
    if (mountedRef.current) setYearValue(value);
    return result;
  }, [locale, localize, maxYearAllowed, minYearAllowed, mountedRef, formatedMinYear, formatedMaxYear]);
  const integerValidator = useNumericValidationAdapter({
    validateInputFunction: validateYearInput,
    isRequired: true,
    allowIntegersOnly: true,
    minLength: isArSaLocale(locale) ? 3 : 4,
    initialValue: String(props.year),
    disableAutoFormatting: true
  });
  const validateArSalocaleMonth = useCallback((value, year) => {
    const formattedMonth = parseInt(monthFormat(new Date(year, value, props.focusDate)));
    const formattedYear = parseInt(yearFormat(new Date(year, value, props.focusDate)));
    if (formattedYear === formatedMinYear && formattedMonth < minMonthFormated) {
      setIsMonthValid(false);
      return;
    } else if (formattedYear === formatedMaxYear && formattedMonth > maxMonthFormated) {
      setIsMonthValid(false);
      return;
    } else {
      return setIsMonthValid(true);
    }
  }, [monthFormat, yearFormat, props.focusDate, formatedMaxYear, formatedMinYear, maxMonthFormated, minMonthFormated]);
  const validateMonthValue = useCallback((value, year) => {
    const date = new Date(year, value, 5);
    const testMinDate = new Date(minDate);
    testMinDate.setDate(1);
    if (isThaiLocale(locale)) testMinDate.setFullYear(convertGregorianYearToThaiYear(testMinDate.getFullYear()));
    const testMaxDate = new Date(maxDate);
    testMaxDate.setDate(27); // All months have day 27
    if (isThaiLocale(locale)) testMaxDate.setFullYear(convertGregorianYearToThaiYear(testMaxDate.getFullYear()));
    if (date.getTime() < testMinDate.getTime() || date.getTime() > testMaxDate.getTime()) setIsMonthValid(false);else setIsMonthValid(true);
  }, [locale]);
  function handleChange(e) {
    setYearValue2(e.value);
    if (!mountedRef.current) return;
    integerValidator.handleChange(e);
    if (e.value.length >= 4 || isArSaLocale(locale) && e.value.length >= 3) {
      const result = validateYearInput(e.value, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
      if (!result.isError) {
        if (isArSaLocale(locale)) {
          const monthIndex = monthValueFormated - 1;
          const {
            year,
            month,
            day
          } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(e.value), monthIndex, monthIndex);
          props.onChange({
            month,
            year,
            day
          });
          props.onYearChange(getDisplayedDate(month, year));
          validateArSalocaleMonth(month, year);
        } else validateMonthValue(props.month, parseInt(e.value));
      }
    }
  }
  function handleBlur() {
    if (!mountedRef.current) return;
    const year = isArSaLocale(locale) ? yearValueFormated : yearValue;
    if (!isNaN(parseInt(year)) || year.trim() === '') {
      const result = validateYearInput(year, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
      if (result.isError) {
        integerValidator.handleBlur(); // Show error in control
        return;
      }
    }
  }
  const reset = useCallback(() => {
    if (!isArSaLocale(locale)) {
      setYearValue(String(props.year));
      validateYearInput(String(props.year), locale, true, true, false, 4);
      validateMonthValue(props.month, props.year);
    }
  }, [locale, props.month, props.year, validateMonthValue, validateYearInput]);
  useEffect(() => {
    reset();
  }, [reset]);
  useEffect(() => {
    if (isArSaLocale(locale)) {
      validateArSalocaleMonth(props.month, props.year);
    }
  }, [validateArSalocaleMonth, props.month, props.year, locale]);
  const refSelect = useRef(null);
  const refInput = useRef(null);
  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!isValid || !isMonthValid) return false;
      return true;
    },
    focus: () => !isMonthValid ? refSelect.current?.focus() : refInput.current?.focus(),
    reset
  }));
  const minMaxMonthErrorMsg = localize('minMaxMonth.error.msg', {
    min: getMonthAndYearByDate(locale, minDate),
    max: getMonthAndYearByDate(locale, maxDate)
  });
  const getDisplayedDate = (month, year) => {
    const lastday = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, props.focusDate > lastday ? lastday : props.focusDate);
  };
  const convertDateToGregorian = useCallback((day, month, year, localeYear, currentMonthIndex, selectedMonthIndex) => {
    let currentYear = localeYear;
    let currentMonth = selectedMonthIndex;
    let currentDay = day;
    let count = 0;
    let newDate = new Date();
    let formatedYear = parseInt(yearFormat(newDate));
    let yearDifference = formatedYear - newDate.getFullYear();
    if (!isArSaLocale(locale) || yearDifference === 0) return {
      day,
      month,
      year: localeYear
    };
    while (yearDifference !== 0 && count < 5) {
      count++;
      currentYear -= yearDifference;
      newDate = new Date(currentYear, currentMonth, currentDay);
      formatedYear = parseInt(yearFormat(newDate));
      yearDifference = formatedYear - localeYear;
    }
    count = 0;
    while (yearDifference !== 0 && count < 12) {
      count++;
      if (yearDifference < 0) {
        currentMonth++;
      } else if (yearDifference > 0) {
        currentMonth--;
      }
      newDate = new Date(currentYear, currentMonth, currentDay);
      formatedYear = parseInt(yearFormat(newDate));
      yearDifference = formatedYear - localeYear;
    }
    if (typeof currentMonthIndex === 'number' && yearDifference === 0) {
      const selectedFormatedMonth = currentMonthIndex + 1;
      let formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
      let monthDifference = formatedMonth - selectedFormatedMonth;
      count = 0;
      while (monthDifference !== 0 && yearDifference === 0 && count < 12) {
        count++;
        if (monthDifference > 0) {
          currentMonth--;
        } else if (monthDifference < 0) {
          currentMonth++;
        }
        newDate = new Date(currentYear, currentMonth, currentDay);
        formatedYear = parseInt(yearFormat(newDate));
        yearDifference = formatedYear - localeYear;
        if (yearDifference > 0) {
          currentMonth--;
          break;
        } else if (yearDifference < 0) {
          currentMonth++;
          break;
        } else {
          formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
          monthDifference = formatedMonth - selectedFormatedMonth;
        }
      }
      formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
      monthDifference = formatedMonth - selectedFormatedMonth;
      count = 0;
      while (monthDifference !== 0 && count < 31) {
        count++;
        if (monthDifference > 0) {
          currentDay--;
        } else if (monthDifference < 0) {
          currentDay++;
        }
        newDate = new Date(currentYear, currentMonth, currentDay);
        formatedMonth = parseInt(monthFormat(new Date(currentYear, currentMonth, currentDay)));
        monthDifference = formatedMonth - selectedFormatedMonth;
      }
    }
    const dayDifference = parseInt(dayFormat(new Date(currentYear, currentMonth, currentDay))) - parseInt(dayFormat(new Date(year, month, day)));
    if (dayDifference !== 0) currentDay -= dayDifference;
    const newDateCreated = new Date(currentYear, currentMonth, currentDay);
    return {
      year: newDateCreated.getFullYear(),
      month: newDateCreated.getMonth(),
      day: newDateCreated.getDate()
    };
  }, [yearFormat, monthFormat, locale, dayFormat]);
  const yearElement = /*#__PURE__*/_jsx(Input, {
    ref: refInput,
    testId: "calendarHeaderYearInput",
    className: cn.headerYearInput,
    value: isArSaLocale(locale) ? yearValueFormated : yearValue,
    onCommit: e => {
      if (!mountedRef.current) return;
      if (e.reason === 'blur' && props.year === parseInt(e.value)) return;
      if (e.reason === 'enter') {
        const result = validateYearInput(e.value, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
        if (result.isError) {
          integerValidator.handleBlur(); // Show error in control
          return;
        }
      }
      if (isValid) {
        if (isArSaLocale(locale)) {
          const monthIndex = monthValueFormated - 1;
          const {
            year,
            month,
            day
          } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(e.value), monthIndex, monthIndex);
          props.onChange({
            month,
            year,
            day
          });
          props.onYearChange(getDisplayedDate(month, year));
          setYearValue2(null);
        } else {
          props.onChange({
            month: props.month,
            year: parseInt(e.value)
          });
          props.onYearChange(getDisplayedDate(props.month, parseInt(e.value)));
        }
      }
    },
    onFocus: () => {
      if (!mountedRef.current) return;
      const year = isArSaLocale(locale) ? yearValueFormated : yearValue;
      if (!isNaN(parseInt(year)) || year.trim() === '') {
        const result = validateYearInput(year, locale, true, true, false, isArSaLocale(locale) ? 3 : 4);
        if (result.isError) {
          integerValidator.handleBlur(); // Show error in control
          return;
        }
      }
    },
    onChange: handleChange,
    onBlur: handleBlur,
    status: isValid ? undefined : 'alert',
    message: integerValidator.errors.message,
    maxLength: isThaiLocale(locale) ? 5 : 4,
    ariaLabel: localize('year.aria'),
    required: true
  });
  return /*#__PURE__*/_jsxs("div", {
    className: cn.header,
    children: [/*#__PURE__*/_jsx(Button, {
      icon: {
        icon: DisclosurePrevious,
        tooltip: localize('previousMonth.aria')
      },
      variant: ButtonVariant.Icon,
      focusable: false,
      onPress: () => {
        const month = props.month === 0 ? 11 : props.month - 1;
        const year = props.month === 0 ? props.year - 1 : props.year;
        props.onChange({
          month,
          year
        });
        props.onPreviousButtonPress(getDisplayedDate(month, year));
        if (props.month === 0) setYearValue(String(props.year - 1));
      },
      testId: "calendarHeaderPreviousButton",
      disabled: !isValid || (isArSaLocale(locale) ? parseInt(yearValueFormated) === formatedMinYear && monthValueFormated <= minMonthFormated : props.month <= minMonthAllowed && props.year === (isThaiLocale(locale) ? convertGregorianYearToThaiYear(minYearAllowed) : minYearAllowed))
    }), /*#__PURE__*/_jsxs("div", {
      className: cn.headerMonthYearContainer,
      children: [yearOrder === 'before' && yearElement, /*#__PURE__*/_jsx(Select, {
        testId: "calendarHeaderMonthSelect",
        selectBoxRef: refSelect,
        className: cn.headerMonthButton,
        popoverClassName: props.selectClassName,
        selectedIndex: props.formatedMonth,
        values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(monthIndex => ({
          text: months[monthIndex]
        })),
        onSelected: value => {
          if (typeof value === 'number' && !isNaN(parseInt(yearValue)) && isValid) {
            if (!isArSaLocale(locale)) {
              props.onChange({
                month: value,
                year: parseInt(yearValue)
              });
              props.onMonthSelect(getDisplayedDate(value, props.year));
              validateMonthValue(value, parseInt(yearValue));
              return;
            }
            const monthDifference = props.formatedMonth - value;
            let currentMonth = monthDifference > 0 ? props.month - monthDifference : props.month + Math.abs(monthDifference);
            if (currentMonth < 0) {
              currentMonth = 12 - Math.abs(currentMonth);
            }
            if (currentMonth >= 12) {
              currentMonth = currentMonth - 12;
            }
            const {
              year,
              month,
              day
            } = convertDateToGregorian(props.focusDate, props.month, props.year, parseInt(yearValueFormated), value, currentMonth);
            props.onChange({
              month,
              year,
              day
            });
            props.onMonthSelect(getDisplayedDate(month, year));
            validateArSalocaleMonth(month, year);
          }
        },
        ariaLabel: localize('month.aria'),
        message: isValid && !isMonthValid ? minMaxMonthErrorMsg : undefined,
        status: isValid && !isMonthValid ? 'alert' : undefined,
        disabled: !isValid
      }), yearOrder === 'after' && yearElement]
    }), /*#__PURE__*/_jsx(Button, {
      icon: {
        icon: DisclosureNext,
        tooltip: localize('nextMonth.aria')
      },
      variant: ButtonVariant.Icon,
      focusable: false,
      onPress: () => {
        const month = props.month === 11 ? 0 : props.month + 1;
        const year = props.month === 11 ? props.year + 1 : props.year;
        props.onChange({
          month,
          year
        });
        props.onNextButtonPress(getDisplayedDate(month, year));
        if (props.month === 11) setYearValue(String(props.year + 1));
      },
      testId: "calendarHeaderNextButton",
      disabled: !isValid || (isArSaLocale(locale) ? parseInt(yearValueFormated) === formatedMaxYear && monthValueFormated >= maxMonthFormated : props.month >= maxMonthAllowed && props.year === (isThaiLocale(locale) ? convertGregorianYearToThaiYear(maxYearAllowed) : maxYearAllowed))
    })]
  });
});
const WeekdayHeaderCells = /*#__PURE__*/memo(function WeekdayHeaderCells(props) {
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const weekdays = useMemo(() => getDaysOfWeek(locale), [locale]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [props.showWeekNumbers ? /*#__PURE__*/_jsx("div", {
      role: "gridcell"
    }) : null, [0, 1, 2, 3, 4, 5, 6].map(weekdayIndex => /*#__PURE__*/_jsx("div", {
      id: props.dayOfWeekIds[weekdayIndex],
      role: "columnheader",
      "aria-label": localize(`weekday.${weekdayIndex}.aria`),
      className: cn.weekdayHeaderCell,
      style: {
        gridColumnStart: `day-start ${1 + weekdayIndex}`
      },
      children: weekdays[weekdayIndex]
    }, weekdayIndex))]
  });
});
const DayCell = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function DayCell(props, ref) {
  const invisibleTodayId = useCalendarTodayId();
  const invisibleSpecialDayId = useCalendarSpecialDayId();
  const invisibleNonWorkDayId = useCalendarNonWorkDayId();
  const localize = useTextLocalizer();
  const getDayCellAriaDescribedBy = () => {
    const ariaDescribedBy = [];
    if (props.today) {
      ariaDescribedBy.push(invisibleTodayId);
    }
    if (props.specialDay) {
      ariaDescribedBy.push(invisibleSpecialDayId);
    }
    if (props.nonWorkDay) {
      ariaDescribedBy.push(invisibleNonWorkDayId);
    }
    ariaDescribedBy.push(props.ariaDescribedBy);
    return ariaDescribedBy.join(' ');
  };
  const getSpecialDayText = () => props.specialDayAriaLabel ?? localize('specialDay.aria');
  const getNonWorkDayText = () => props.nonWorkDayAriaLabel ?? localize('weekend.aria');
  const getMultiSelectMessage = () => localize('multiSelect.msg');
  const getIntervalSelectMessage = () => localize('intervalSelect.msg');
  const getTooltip = () => {
    let tooltip = '';
    if (props.specialDay) {
      tooltip = getSpecialDayText();
    }
    if (props.nonWorkDay) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getNonWorkDayText()}`;
      } else {
        tooltip = getNonWorkDayText();
      }
    }
    if (!props.singleSelection) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getMultiSelectMessage()}`;
      } else {
        tooltip = getMultiSelectMessage();
      }
    } else if (props.intervalSelection) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getIntervalSelectMessage()}`;
      } else {
        tooltip = getIntervalSelectMessage();
      }
    }
    return tooltip;
  };
  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: getTooltip()
  });
  const getTitle = () => {
    let title = '';
    if (props.specialDay) {
      title = getSpecialDayText();
    }
    if (props.nonWorkDay) {
      if (title.length > 0) {
        title += `\n${getNonWorkDayText()}`;
      } else {
        title = getNonWorkDayText();
      }
    }
    return title;
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      role: "gridcell",
      "data-testid": props.testId,
      ref: el => {
        setKeyboardTooltipRef(el);
        setRef(ref, el);
      },
      className: cx(cn.dayCell, !props.inCurrentMonth && cn.dayCellExternal, props.today && cn.dayCellToday, props.focused && props.focusVisible && cn.dayCellFocused, !props.disabled && props.selected && cn.dayCellSelected, !props.disabled && props.selected && props.inInterval && cn.dayCellInterval, props.disabled && cn.dayCellDisabled, props.nonWorkDay && props.inCurrentMonth && cn.nonWorkDay),
      tabIndex: props.focused ? 0 : -1,
      "aria-label": props.inCurrentMonth ? props.ariaLabel : undefined,
      "aria-describedby": getDayCellAriaDescribedBy(),
      "aria-selected": props.disabled ? undefined : props.selected,
      "aria-disabled": props.disabled || undefined,
      onClick: event => {
        if (event.button === 0) {
          props.onPress(props.day);
        }
      },
      onKeyDown: event => {
        switch (event.key) {
          case ' ':
          case 'Spacebar':
          case 'ArrowUp':
          case 'ArrowDown':
          case 'PageUp':
          case 'PageDown':
            event.preventDefault();
            break;
        }
      },
      onBlur: props.onBlur,
      title: getTitle(),
      children: props.specialDay ? /*#__PURE__*/_jsx("div", {
        className: cx(cn.specialDay, props.selected && cn.selected),
        children: /*#__PURE__*/_jsx("span", {
          className: cn.dayCellContent,
          children: props.children
        })
      }) : /*#__PURE__*/_jsx("span", {
        className: cn.dayCellContent,
        children: props.children
      })
    }), props.today && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTodayId,
      testId: postfixTestId(props.testId, '-today'),
      children: localize('today.description.aria')
    }), props.specialDay && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleSpecialDayId,
      children: props.specialDayAriaLabel ?? localize('specialDay.aria')
    }), props.nonWorkDay && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleNonWorkDayId,
      children: props.nonWorkDayAriaLabel ?? localize('weekend.aria')
    })]
  });
}));
export const getCalendarDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'row-gap': dt.tokens['space-block-xs'],
      'col-gap': dt.tokens['space-inline-s'],
      'header-row-gap': dt.tokens['space-block-m'],
      'font-size-xxs': dt.tokens['font-size-body-text-xxs'],
      'font-size-s': dt.tokens['font-size-body-text-s'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'font-weight': dt.tokens['font-style-weight-400'],
      'line-height': dt.tokens['font-space-line-height'],
      'calendar-row-template': `repeat(${opts?.showTodayButton ? opts?.formatedCalendar.length : 7}, 1.5rem)`,
      'today-button-row-template': `repeat(${opts?.showLegend ? 1 : 7 - opts?.formatedCalendar.length}, 1.5rem)`,
      'focus-outline-offset': 'calc(-1px + var(--focus-width) * -1)',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'focus-outline-offset': 'calc(-2px + var(--focus-width) * -1)'
      })
    }, 'calendar-cell'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-subtle-normal'],
      bd: dt.tokens['color-action-border-subtle-normal'],
      fg: dt.tokens['color-action-text-subtle-normal']
    }, 'day-cell-normal'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-standard-hover'],
      fg: dt.tokens['color-action-text-standard-hover']
    }, 'day-cell-hover'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-secondary-selected'],
      bd: dt.tokens['color-action-border-secondary-selected'],
      fg: dt.tokens['color-action-text-secondary-selected']
    }, 'day-cell-selected'),
    ...renderDesignTokens({
      bg: dt.stateStandardContentNova.disabled,
      bd: dt.tokens['color-action-border-subtle-disabled'],
      fg: dt.tokens['color-action-text-subtle-disabled']
    }, 'day-cell-external'),
    ...renderDesignTokens({
      pd: dt.tokens['space-squish-xs']
    }, 'day-cell'),
    ...renderDesignTokens({
      outline: dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)'
    }, 'day-cell-selected-focus'),
    ...renderDesignTokens({
      outline: 'var(--focus-light-bg)'
    }, 'day-cell-interval-focus'),
    ...renderDesignTokens({
      bd: dt.tokens['color-primary'],
      fg: dt.tokens['color-text-primary'],
      bg: 'transparent'
    }, 'day-cell-today'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-subtle-normal'],
      'col-grid-gap': dt.tokens['space-base-null']
    }, 'weekday'),
    ...renderDesignTokens({
      'font-size': dt.tokens['font-size-body-text-s'],
      'font-weight': dt.tokens['font-style-weight-400'],
      fg: dt.tokens['color-label-text'],
      pd: dt.tokens['space-squish-xs']
    }, 'week-number-cell'),
    ...renderDesignTokens({
      bg: dt.tokens['color-container-border-4'],
      bw: dt.tokens['size-border-width-s']
    }, 'calendar-divider'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-subtle-selected-inactive'],
      bd: dt.tokens['color-action-border-subtle-selected-inactive'],
      fg: dt.tokens['color-action-text-subtle-selected-inactive']
    }, 'day-cell-interval'),
    ...renderDesignTokens({
      fg: dt.tokens['color-body-text']
    }, 'special-day'),
    ...renderDesignTokens({
      bg: setAlpha(dt.tokens['color-neutral'], 0.08),
      fg: dt.tokens['color-action-text-subtle-normal'],
      bd: dt.themeVariant === 'high-contrast' ? dt.tokens['color-body-text'] : 'transparent'
    }, 'non-work-day'),
    ...renderDesignTokens({
      'legend-gap': dt.tokens['space-base-m'],
      'font-size-s': dt.tokens['font-size-body-text-s'],
      'font-size-m': dt.tokens['font-size-body-text-m']
    }, 'calendar-legend'),
    ...(!dt.enableNovaTokens && getCalendarLegacyDesignTokens(dt))
  };
};
export const getCalendarLegacyDesignTokens = dt => {
  let weekday, dayCellToday, calendarDivider, dayCellInterval, specialDay, nonWorkDay;
  switch (dt.themeVariant) {
    case 'dark':
      weekday = {
        fg: dt.c.white80
      };
      dayCellToday = {
        fg: dt.c.p2,
        bg: 'transparent',
        bd: dt.c.p
      };
      calendarDivider = {
        bg: dt.c.white15
      };
      dayCellInterval = {
        fg: dt.c.white,
        bg: 'transparent',
        bd: dt.reportTokens.stateSecondary.selected.bd
      };
      specialDay = {
        fg: dt.c.white80
      };
      nonWorkDay = {
        bg: dt.reportTokens.stateStandard.normal.bg,
        bd: 'transparent'
      };
      break;
    case 'high-contrast':
    default:
      weekday = {
        fg: dt.c.n_1
      };
      dayCellToday = {
        fg: dt.c.p,
        bg: 'transparent',
        bd: dt.c.p
      };
      calendarDivider = {
        bg: dt.c.n3
      };
      dayCellInterval = {
        fg: dt.themeVariant === 'high-contrast' ? dt.c.white : dt.reportTokens.stateSecondary.selected.fg,
        bg: dt.themeVariant === 'high-contrast' ? 'transparent' : dt.reportTokens.stateSecondary.selected.bg,
        bd: dt.themeVariant === 'high-contrast' ? dt.reportTokens.stateSecondary.selected.bd : 'transparent'
      };
      specialDay = {
        fg: dt.c.n_1
      };
      nonWorkDay = {
        bg: dt.reportTokens.stateStandard.normal.bg,
        bd: dt.themeVariant === 'high-contrast' ? dt.tokens['color-body-text'] : 'transparent'
      };
      break;
  }
  return {
    ...renderDesignTokens({
      'font-size-xxs': dt.reportTokens.font.size.xxs,
      'font-size-s': dt.reportTokens.font.size.s,
      'font-size-m': dt.reportTokens.font.size.m,
      'font-weight': dt.reportTokens.font.weight.thick
    }, 'calendar-cell'),
    ...renderDesignTokens(dt.reportTokens.stateStandardContent.normal, 'day-cell-normal'),
    ...renderDesignTokens(dt.reportTokens.stateStandardContent.hover, 'day-cell-hover'),
    ...renderDesignTokens(dt.reportTokens.stateStandardContent.disabled, 'day-cell-external'),
    ...renderDesignTokens(dt.reportTokens.stateSecondary.selected, 'day-cell-selected'),
    ...renderDesignTokens(dayCellToday, 'day-cell-today'),
    ...renderDesignTokens(weekday, 'weekday'),
    ...renderDesignTokens(calendarDivider, 'calendar-divider'),
    ...renderDesignTokens(dayCellInterval, 'day-cell-interval'),
    ...renderDesignTokens(specialDay, 'special-day'),
    ...renderDesignTokens(nonWorkDay, 'non-work-day'),
    ...renderDesignTokens({
      'font-size-s': dt.reportTokens.font.size.s,
      'font-size-m': dt.reportTokens.font.size.m
    }, 'calendar-legend')
  };
};
//# sourceMappingURL=Calendar.js.map